import {
  registerCustomMcpServer,
  suggestCustomMcpServer,
  updateCustomMcpServer,
} from "@/components/connectors/api.ts";
import { Alert, AlertDescription } from "@/components/ui/alert.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { apiErrorMessage } from "@/lib/http.ts";
import { cn, resolveApiPath } from "@/lib/utils.ts";
import type { ConnectorCatalogItem } from "@/types/connectors.ts";
import { CUSTOM_MCP_AUTH_OPTIONS, customMcpAuthModeFor } from "@/shared/connectors/customMcp.ts";
import type {
  McpAuthMode,
  RegisterCustomMcpServerResponse,
  SuggestCustomMcpServerResponse,
} from "@/types/mcp.ts";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";

/**
 * Which server the dialog is about. `add` registers a new one from a URL;
 * `edit` changes the name and description of one the account already owns.
 */
export type CustomMcpDialogTarget =
  | { kind: "add" }
  | { kind: "edit"; serverId: number; item: ConnectorCatalogItem };

/**
 * Three chips standing in for a radio group: the probe usually answers this
 * question, so the control reads as a confirmation rather than a form field.
 */
const AuthModeChooser = ({
  value,
  onChange,
}: {
  value: McpAuthMode | null;
  onChange: (next: McpAuthMode) => void;
}) => (
  <div role="radiogroup" aria-label="Sign-in method" className="flex flex-wrap gap-2">
    {CUSTOM_MCP_AUTH_OPTIONS.map((option) => {
      const selected = option.value === value;
      return (
        <Button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={selected}
          variant={selected ? "primary" : "outline"}
          size="xs"
          onClick={() => onChange(option.value)}
          data-statsig-name="connectors-custom-mcp-auth-choice"
        >
          {option.label}
        </Button>
      );
    })}
  </div>
);

const submitLabel = (pending: boolean, editing: boolean): string => {
  if (pending) return editing ? "Saving…" : "Adding…";
  return editing ? "Save" : "Add";
};

type CustomMcpFormValues = {
  url: string;
  name: string;
  description: string;
  selectionCriteria: string;
  /** Null until the probe answers or the user picks. */
  authMode: McpAuthMode | null;
  apiKey: string;
};

const initialValues = (target: CustomMcpDialogTarget): CustomMcpFormValues => ({
  url: "",
  name: target.kind === "edit" ? target.item.name : "",
  description: target.kind === "edit" ? target.item.description : "",
  selectionCriteria: "",
  authMode: null,
  apiKey: "",
});

/** The server's reason for a failed Add or Save, or our fallback wording; null when neither failed. */
const submitErrorFor = (
  register: { isError: boolean; error: unknown },
  update: { isError: boolean; error: unknown },
): string | null => {
  if (register.isError) {
    return apiErrorMessage(register.error) ?? "Couldn't add the server. Try again.";
  }
  if (update.isError) {
    return apiErrorMessage(update.error) ?? "Couldn't save the changes. Try again.";
  }
  return null;
};

/**
 * Add a custom MCP server from its URL, or rename one.
 *
 * Adding runs in two steps. Check posts the URL to the suggest endpoint, which
 * probes the server and comes back with a proposed name and copy, the sign-in
 * method it detected, and whether this account can already connect a server at
 * that URL. The fields then open prefilled for the user to adjust before Add
 * registers the server. A server the probe could not classify leaves the
 * sign-in choice to the user; one that needs an admin-registered OAuth app
 * explains that and offers nothing to add.
 *
 * Add leaves the dialog on a last pane with the new server's Connect link
 * rather than opening it itself: the connect is a redirect to the service, and
 * a tab opened after an await is what popup blockers eat. An API-key server
 * connects during Add and skips that pane.
 */
export const CustomMcpDialog = ({
  open,
  onOpenChange,
  target,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: CustomMcpDialogTarget;
}) => {
  const queryClient = useQueryClient();
  const editing = target.kind === "edit";
  const [probe, setProbe] = useState<SuggestCustomMcpServerResponse | null>(null);
  const [added, setAdded] = useState<RegisterCustomMcpServerResponse | null>(null);
  const urlId = useId();
  const nameId = useId();
  const descriptionId = useId();
  const criteriaId = useId();
  const apiKeyId = useId();

  const invalidate = () => {
    for (const key of ["connectorCatalog", "connectors", "currentUser"]) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  };

  const form = useForm({
    defaultValues: initialValues(target),
    onSubmit: ({ value }) => {
      if (target.kind === "edit") {
        update.mutate({
          serverId: target.serverId,
          name: value.name.trim(),
          description: value.description.trim(),
        });
        return;
      }
      if (!probe || !value.authMode) return;
      register.mutate({
        url: probe.canonicalUrl,
        name: value.name.trim(),
        description: value.description.trim(),
        selectionCriteria: value.selectionCriteria.trim() || undefined,
        authMode: value.authMode,
        ...(value.authMode === "api_key" ? { apiKey: value.apiKey } : {}),
      });
    },
  });

  // The dialog stays mounted while closed, so a probe result and its fields
  // would otherwise greet the next open.
  const close = () => {
    form.reset();
    suggest.reset();
    register.reset();
    update.reset();
    setProbe(null);
    setAdded(null);
    onOpenChange(false);
  };

  const suggest = useMutation({
    mutationFn: (url: string) => suggestCustomMcpServer({ url }),
    onSuccess: (result, url) => {
      // The field may have moved on while the probe ran. A result for a URL
      // the user no longer shows must not open its details: Add would then
      // register the old server under the new address.
      if (form.getFieldValue("url").trim() !== url) return;
      setProbe(result);
      form.setFieldValue("name", result.suggestions.name);
      form.setFieldValue("description", result.suggestions.description ?? "");
      form.setFieldValue("selectionCriteria", result.suggestions.selectionCriteria ?? "");
      form.setFieldValue("authMode", customMcpAuthModeFor(result.authChoice));
    },
  });

  const register = useMutation({
    mutationFn: registerCustomMcpServer,
    onSuccess: (result) => {
      invalidate();
      // Connected in the same call: the tile flipping to Connected is the
      // confirmation, as it is for every other connect.
      if (result.connected) close();
      else setAdded(result);
    },
  });

  const update = useMutation({
    mutationFn: ({ serverId, ...req }: { serverId: number; name: string; description: string }) =>
      updateCustomMcpServer(serverId, req),
    onSuccess: () => {
      invalidate();
      close();
    },
  });

  const checkError = suggest.isError
    ? (apiErrorMessage(suggest.error) ?? "Couldn't reach that server. Check the URL and try again.")
    : null;
  const submitError = submitErrorFor(register, update);

  const adminRequired = probe?.authChoice === "admin_required";
  const alreadyKnown = probe !== null && probe.existingServerId !== null;
  const showDetails = editing || (probe !== null && !adminRequired && !alreadyKnown);

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editing ? `Edit ${target.item.name}` : "Add a custom MCP server"}
          </DialogTitle>
          <DialogDescription>
            {editing
              ? "Only you can see this connector."
              : "Connect any server that speaks MCP. Only you will see it."}
          </DialogDescription>
        </DialogHeader>

        {added ? (
          <div className="space-y-3">
            {added.connectionError ? (
              <Alert variant="destructive">
                <AlertDescription>
                  {added.item.name} was added, but connecting failed: {added.connectionError} You
                  can try again from its tile.
                </AlertDescription>
              </Alert>
            ) : (
              <p className="text-sm text-muted-foreground">
                {added.item.name} is in your connectors. Connect it to start using it.
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={close}
                data-statsig-name="connectors-custom-mcp-done"
              >
                Done
              </Button>
              {added.item.connectPath && !added.connectionError && (
                <Button
                  variant="primary"
                  size="sm"
                  asChild
                  data-statsig-name="connectors-custom-mcp-connect"
                >
                  <a
                    href={resolveApiPath(added.item.connectPath)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={close}
                  >
                    Connect
                  </a>
                </Button>
              )}
            </DialogFooter>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
            className="space-y-3"
          >
            {!editing && (
              <form.Field name="url">
                {(field) => (
                  <div className="space-y-1">
                    <Label htmlFor={urlId}>Server URL</Label>
                    <div className="flex gap-2">
                      <Input
                        id={urlId}
                        type="url"
                        inputMode="url"
                        autoComplete="off"
                        placeholder="https://mcp.example.com/mcp"
                        required
                        value={field.state.value}
                        onChange={(e) => {
                          field.handleChange(e.target.value);
                          // A new URL invalidates what the probe said about the old one.
                          if (probe) setProbe(null);
                        }}
                        onBlur={field.handleBlur}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="default"
                        className="shrink-0"
                        disabled={suggest.isPending || !field.state.value.trim()}
                        onClick={() => suggest.mutate(field.state.value.trim())}
                        data-statsig-name="connectors-custom-mcp-check"
                      >
                        {suggest.isPending ? "Checking…" : "Check"}
                      </Button>
                    </div>
                    {checkError && (
                      <p role="alert" className="text-xs text-destructive">
                        {checkError}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>
            )}

            {adminRequired && (
              <Alert variant="destructive">
                <AlertDescription>
                  This server runs OAuth but does not let apps register themselves, so it needs an
                  OAuth app set up by an okthx admin.
                </AlertDescription>
              </Alert>
            )}

            {alreadyKnown && (
              <Alert>
                <AlertDescription>
                  This server is already in your connectors. Connect it from its tile.
                </AlertDescription>
              </Alert>
            )}

            {showDetails && (
              <>
                <form.Field
                  name="name"
                  validators={{
                    onChange: ({ value }) => (value.trim() ? undefined : "A name is required"),
                  }}
                >
                  {(field) => (
                    <div className="space-y-1">
                      <Label htmlFor={nameId}>Name</Label>
                      <Input
                        id={nameId}
                        autoComplete="off"
                        required
                        maxLength={100}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                    </div>
                  )}
                </form.Field>
                <form.Field name="description">
                  {(field) => (
                    <div className="space-y-1">
                      <Label htmlFor={descriptionId}>Description</Label>
                      <Textarea
                        id={descriptionId}
                        rows={2}
                        maxLength={500}
                        placeholder="What this server does"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                    </div>
                  )}
                </form.Field>
                {!editing && (
                  <form.Field name="selectionCriteria">
                    {(field) => (
                      <div className="space-y-1">
                        <Label htmlFor={criteriaId}>When the assistant should use it</Label>
                        <Textarea
                          id={criteriaId}
                          rows={2}
                          maxLength={500}
                          placeholder="Use it when I ask about…"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                        />
                      </div>
                    )}
                  </form.Field>
                )}
                {!editing && (
                  <form.Field name="authMode">
                    {(field) => (
                      <div className="space-y-1">
                        <Label>Sign-in method</Label>
                        <AuthModeChooser value={field.state.value} onChange={field.handleChange} />
                        {field.state.value === null && (
                          <p className="text-xs text-muted-foreground">
                            We couldn't tell how this server signs you in. Pick one.
                          </p>
                        )}
                      </div>
                    )}
                  </form.Field>
                )}
                {!editing && (
                  <form.Subscribe selector={(state) => state.values.authMode}>
                    {(authMode) =>
                      authMode === "api_key" && (
                        <form.Field name="apiKey">
                          {(field) => (
                            <div className="space-y-1">
                              <Label htmlFor={apiKeyId}>API key</Label>
                              <Input
                                id={apiKeyId}
                                type="password"
                                autoComplete="off"
                                required
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e.target.value)}
                                onBlur={field.handleBlur}
                              />
                              <p className="text-xs text-muted-foreground">
                                Stored encrypted and sent only to this server.
                              </p>
                            </div>
                          )}
                        </form.Field>
                      )
                    }
                  </form.Subscribe>
                )}
              </>
            )}

            {submitError && (
              <p role="alert" className="text-sm text-destructive">
                {submitError}
              </p>
            )}

            {showDetails && (
              <DialogFooter>
                <form.Subscribe selector={(state) => [state.values.authMode, state.canSubmit]}>
                  {([authMode, canSubmit]) => (
                    <Button
                      type="submit"
                      size="sm"
                      className={cn(!canSubmit && "opacity-60")}
                      disabled={
                        register.isPending || update.isPending || (!editing && authMode === null)
                      }
                      data-statsig-name={
                        editing ? "connectors-custom-mcp-save" : "connectors-custom-mcp-add"
                      }
                    >
                      {submitLabel(register.isPending || update.isPending, editing)}
                    </Button>
                  )}
                </form.Subscribe>
              </DialogFooter>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
